import React from "react";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Adjuster } from "./Adjuster";
import { IPlayer } from "../../PersonObjects/IPlayer";

const bigNumber = 1e27;

interface IProps {
  player: IPlayer;
}

export function Stats(props: IProps): React.ReactElement {
  function modifyExp(stat: string, modifier: number) {
    return function (exp: number) {
      switch (stat) {
        case "hacking":
          if (exp) {
            props.player.gainHackingExp(exp * modifier);
          }
          break;
        case "strength":
          if (exp) {
            props.player.gainStrengthExp(exp * modifier);
          }
          break;
        case "defense":
          if (exp) {
            props.player.gainDefenseExp(exp * modifier);
          }
          break;
        case "dexterity":
          if (exp) {
            props.player.gainDexterityExp(exp * modifier);
          }
          break;
        case "agility":
          if (exp) {
            props.player.gainAgilityExp(exp * modifier);
          }
          break;
        case "charisma":
          if (exp) {
            props.player.gainCharismaExp(exp * modifier);
          }
          break;
        case "intelligence":
          if (exp) {
            props.player.gainIntelligenceExp(exp * modifier);
          }
          break;
      }
      props.player.updateSkillLevels();
    };
  }

  function modifyKarma(modifier: number) {
    return function (amt: number) {
      props.player.karma += amt * modifier;
    };
  }

  function tonsOfExp(): void {
    props.player.gainHackingExp(bigNumber);
    props.player.gainStrengthExp(bigNumber);
    props.player.gainDefenseExp(bigNumber);
    props.player.gainDexterityExp(bigNumber);
    props.player.gainAgilityExp(bigNumber);
    props.player.gainCharismaExp(bigNumber);
    props.player.gainIntelligenceExp(bigNumber);
    props.player.updateSkillLevels();
  }

  function resetAllExp(): void {
    props.player.exp.hacking = 0;
    props.player.exp.strength = 0;
    props.player.exp.defense = 0;
    props.player.exp.dexterity = 0;
    props.player.exp.agility = 0;
    props.player.exp.charisma = 0;
    props.player.exp.intelligence = 0;
    props.player.updateSkillLevels();
  }

  function resetExperience(stat: string): () => void {
    return function () {
      switch (stat) {
        case "hacking":
          props.player.exp.hacking = 0;
          break;
        case "strength":
          props.player.exp.strength = 0;
          break;
        case "defense":
          props.player.exp.defense = 0;
          break;
        case "dexterity":
          props.player.exp.dexterity = 0;
          break;
        case "agility":
          props.player.exp.agility = 0;
          break;
        case "charisma":
          props.player.exp.charisma = 0;
          break;
        case "intelligence":
          props.player.exp.intelligence = 0;
          break;
      }
      props.player.updateSkillLevels();
    };
  }

  function resetKarma(): () => void {
    return function () {
      props.player.karma = 0;
    };
  }

  function enableIntelligence(): void {
    if (props.player.skills.intelligence === 0) {
      props.player.skills.intelligence = 1;
      props.player.updateSkillLevels();
    }
  }

  function disableIntelligence(): void {
    props.player.exp.intelligence = 0;
    props.player.skills.intelligence = 0;
    props.player.updateSkillLevels();
  }

  return (
    <Accordion TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Experience / Stats</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <table>
          <tbody>
            <tr>
              <td>
                <Typography>All:</Typography>
              </td>
              <td>
                <Button onClick={tonsOfExp}>Tons of exp</Button>
                <Button onClick={resetAllExp}>Reset</Button>
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Hacking:</Typography>
              </td>
              <td>
                <Adjuster
                  label="hacking"
                  placeholder="exp"
                  tons={() => modifyExp("hacking", 1)(bigNumber)}
                  add={modifyExp("hacking", 1)}
                  subtract={modifyExp("hacking", -1)}
                  reset={resetExperience("hacking")}
                />
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Strength:</Typography>
              </td>
              <td>
                <Adjuster
                  label="strength"
                  placeholder="exp"
                  tons={() => modifyExp("strength", 1)(bigNumber)}
                  add={modifyExp("strength", 1)}
                  subtract={modifyExp("strength", -1)}
                  reset={resetExperience("strength")}
                />
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Defense:</Typography>
              </td>
              <td>
                <Adjuster
                  label="defense"
                  placeholder="exp"
                  tons={() => modifyExp("defense", 1)(bigNumber)}
                  add={modifyExp("defense", 1)}
                  subtract={modifyExp("defense", -1)}
                  reset={resetExperience("defense")}
                />
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Dexterity:</Typography>
              </td>
              <td>
                <Adjuster
                  label="dexterity"
                  placeholder="exp"
                  tons={() => modifyExp("dexterity", 1)(bigNumber)}
                  add={modifyExp("dexterity", 1)}
                  subtract={modifyExp("dexterity", -1)}
                  reset={resetExperience("dexterity")}
                />
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Agility:</Typography>
              </td>
              <td>
                <Adjuster
                  label="agility"
                  placeholder="exp"
                  tons={() => modifyExp("agility", 1)(bigNumber)}
                  add={modifyExp("agility", 1)}
                  subtract={modifyExp("agility", -1)}
                  reset={resetExperience("agility")}
                />
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Charisma:</Typography>
              </td>
              <td>
                <Adjuster
                  label="charisma"
                  placeholder="exp"
                  tons={() => modifyExp("charisma", 1)(bigNumber)}
                  add={modifyExp("charisma", 1)}
                  subtract={modifyExp("charisma", -1)}
                  reset={resetExperience("charisma")}
                />
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Intelligence:</Typography>
              </td>
              <td>
                <Adjuster
                  label="intelligence"
                  placeholder="exp"
                  tons={() => modifyExp("intelligence", 1)(bigNumber)}
                  add={modifyExp("intelligence", 1)}
                  subtract={modifyExp("intelligence", -1)}
                  reset={resetExperience("intelligence")}
                />
              </td>
              <td>
                <Button onClick={enableIntelligence}>Enable</Button>
              </td>
              <td>
                <Button onClick={disableIntelligence}>Disable</Button>
              </td>
            </tr>
            <tr>
              <td>
                <Typography>Karma:</Typography>
              </td>
              <td>
                <Adjuster
                  label="karma"
                  placeholder="amt"
                  tons={() => modifyExp("intelligence", 1)(100000)}
                  add={modifyKarma(1)}
                  subtract={modifyKarma(-1)}
                  reset={resetKarma()}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </AccordionDetails>
    </Accordion>
  );
}
